import { cx } from "@unizpay/ui";
import type {
  DeliveryStatus,
  DepositStatus,
  PaymentStatus,
  SettlementKind,
  SettlementStatus,
  TxStatus,
} from "../lib/types";

export type Tone = "neutral" | "info" | "progress" | "ok" | "warn" | "danger";

const tones: Record<Tone, string> = {
  neutral: "bg-subtle text-fg-muted",
  info: "bg-subtle text-fg",
  progress: "bg-info-subtle text-info",
  ok: "bg-ok-subtle text-ok",
  warn: "bg-warn-subtle text-warn",
  danger: "bg-danger-subtle text-danger",
};

/** Every payment status (PAYMENT_STATUSES in @unizpay/shared) with its label and tone. */
const PAYMENT_STATUS_META: Record<PaymentStatus, { label: string; tone: Tone }> = {
  awaiting_payment: { label: "Awaiting payment", tone: "info" },
  detected: { label: "Confirming", tone: "progress" },
  confirmed: { label: "Processing", tone: "progress" },
  swapping: { label: "Processing", tone: "progress" },
  settled: { label: "Succeeded", tone: "ok" },
  settled_raw: { label: "Succeeded, not converted", tone: "warn" },
  underpaid: { label: "Underpaid", tone: "warn" },
  expired: { label: "Expired", tone: "neutral" },
  late: { label: "Late payment", tone: "warn" },
  frozen: { label: "Frozen", tone: "danger" },
  canceled: { label: "Canceled", tone: "neutral" },
  reversed: { label: "Reversed", tone: "danger" },
};

export const SETTLEMENT_KIND_LABEL: Record<SettlementKind, string> = {
  sweep: "Sweep",
  swap: "Swap",
  raw: "Raw delivery",
  bridge: "Bridge (Across)",
  recover: "Recovery",
};

/** Compact rounded status label, as in dense tables. */
export function StatusPill({ tone = "neutral", children }: { tone?: Tone; children: string }) {
  return (
    <span
      className={cx(
        "inline-flex items-center rounded px-1.5 py-px text-xs font-medium whitespace-nowrap",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * `label` (and `tone`) rename the status where a page has its own word for it (e.g.
 * "Deactivated" links, "Not started" payments).
 */
export function PaymentStatusPill({
  status,
  label,
  tone,
}: { status: PaymentStatus; label?: string; tone?: Tone }) {
  const meta = PAYMENT_STATUS_META[status] ?? { label: status, tone: "neutral" as const };
  return <StatusPill tone={tone ?? meta.tone}>{label ?? meta.label}</StatusPill>;
}

const depositTones: Record<DepositStatus, Tone> = {
  detected: "progress",
  confirmed: "progress",
  final: "ok",
  reorged: "danger",
};

export function DepositStatusPill({ status }: { status: DepositStatus }) {
  const label = status === "reorged" ? "Reorged" : status.charAt(0).toUpperCase() + status.slice(1);
  return <StatusPill tone={depositTones[status] ?? "neutral"}>{label}</StatusPill>;
}

const jobTones: Record<SettlementStatus | DeliveryStatus, Tone> = {
  pending: "info",
  submitted: "progress",
  bridging: "progress",
  succeeded: "ok",
  refunded: "warn",
  failed: "danger",
};

export function JobStatusPill({ status }: { status: SettlementStatus | DeliveryStatus }) {
  return (
    <StatusPill tone={jobTones[status] ?? "neutral"}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </StatusPill>
  );
}

const txStatus: Record<TxStatus, { label: string; tone: Tone }> = {
  confirming: { label: "Confirming", tone: "progress" },
  processing: { label: "Processing", tone: "progress" },
  completed: { label: "Completed", tone: "ok" },
  returned: { label: "Returned", tone: "warn" },
  on_hold: { label: "On hold", tone: "danger" },
  failed: { label: "Failed", tone: "danger" },
  reverted: { label: "Reverted", tone: "neutral" },
};

export function TxStatusPill({ status }: { status: TxStatus }) {
  const meta = txStatus[status] ?? { label: status, tone: "neutral" as const };
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
}
